import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

export interface AddWeekScheduleDialogData {
  employeeID: any;
  payrollPeriod: any[];
  weekdays: { value: number; name: string }[];
  isFlexi: boolean;
}

@Component({
  selector: 'app-add-week-schedule-dialog',
  template: `
    <div class="week-schedule-dialog">

      <div mat-dialog-title class="dialog-title">
        <mat-icon>date_range</mat-icon>
        <div>
          <h3>Add Week Schedule</h3>
          <p>Set one schedule template and apply it to multiple days at once</p>
        </div>
        <button mat-icon-button (click)="onCancel()" class="close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content>

        <div class="info-banner">
          <mat-icon>info_outline</mat-icon>
          <span>Select the days you want, fill in the work hours, then click Apply. Each selected day creates its own schedule record.</span>
        </div>

        <form [formGroup]="weekForm">

          <!-- Day Selection -->
          <div class="section-label">Apply to Days</div>
          <div class="day-selector">
            <div class="day-chip"
                 *ngFor="let day of data.weekdays"
                 [class.selected]="selectedDays.has(day.value)"
                 (click)="toggleDay(day.value)">
              {{ day.name.substring(0, 3) }}
            </div>
          </div>
          <div class="day-quick-actions">
            <button mat-button type="button" (click)="selectWorkdays()">Mon – Fri</button>
            <button mat-button type="button" (click)="selectAll()">All Days</button>
            <button mat-button type="button" (click)="clearAll()">Clear</button>
          </div>

          <mat-divider class="my-divider"></mat-divider>

          <!-- Flexi Fields -->
          <ng-container *ngIf="data.isFlexi">
            <div class="section-label">Flexible Schedule</div>
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Start Time</mat-label>
              <input matInput [ngxTimepicker]="tpStart" formControlName="startTime" placeholder="HH:MM">
              <mat-icon matSuffix (click)="tpStart.open()" class="time-icon">access_time</mat-icon>
              <ngx-material-timepicker #tpStart></ngx-material-timepicker>
            </mat-form-field>
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>End Time</mat-label>
              <input matInput [ngxTimepicker]="tpEnd" formControlName="endTime" placeholder="HH:MM">
              <mat-icon matSuffix (click)="tpEnd.open()" class="time-icon">access_time</mat-icon>
              <ngx-material-timepicker #tpEnd></ngx-material-timepicker>
            </mat-form-field>
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Allowed Regular Minutes</mat-label>
              <input matInput type="number" formControlName="allowedRegularMinutes" placeholder="e.g. 540">
            </mat-form-field>
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Allowed Overtime Minutes</mat-label>
              <input matInput type="number" formControlName="allowedOTMinutes" placeholder="e.g. 240">
            </mat-form-field>
          </ng-container>

          <!-- Regular Time In/Out -->
          <ng-container *ngIf="!data.isFlexi && !weekForm.get('isRestDay')?.value">
            <div class="section-label">Work Hours</div>
            <div class="time-pair-row" *ngFor="let pair of [0,1,2]; let i = index">
              <div class="time-pair-label">
                {{ i === 0 ? 'First Time In/Out' : i === 1 ? 'Second Time In/Out' : 'Overtime' }}
              </div>
              <div class="time-pair-fields">
                <mat-form-field appearance="outline" class="time-field">
                  <mat-label>Time In</mat-label>
                  <input matInput [ngxTimepicker]="tpIn" [formControlName]="'rampTime' + (i*2+1)" placeholder="HH:MM">
                  <mat-icon matSuffix (click)="tpIn.open()" class="time-icon">access_time</mat-icon>
                  <ngx-material-timepicker #tpIn></ngx-material-timepicker>
                </mat-form-field>
                <span class="pair-arrow">→</span>
                <mat-form-field appearance="outline" class="time-field">
                  <mat-label>Time Out</mat-label>
                  <input matInput [ngxTimepicker]="tpOut" [formControlName]="'rampTime' + (i*2+2)" placeholder="HH:MM">
                  <mat-icon matSuffix (click)="tpOut.open()" class="time-icon">access_time</mat-icon>
                  <ngx-material-timepicker #tpOut></ngx-material-timepicker>
                </mat-form-field>
              </div>
            </div>
          </ng-container>

          <mat-divider class="my-divider"></mat-divider>
        </form>

        <div class="preview-section" *ngIf="selectedDays.size > 0">
          <div class="section-label">Preview — {{ selectedDays.size }} day(s) will be created</div>
          <div class="preview-chips">
            <span class="preview-chip" *ngFor="let d of selectedDaysArray">
              <mat-icon>work</mat-icon>
              {{ getDayName(d) }}
            </span>
          </div>
        </div>

        <div class="no-days-warning" *ngIf="selectedDays.size === 0">
          <mat-icon>warning_amber</mat-icon> Please select at least one day.
        </div>

      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button mat-button (click)="onCancel()">Cancel</button>
        <button mat-raised-button class="apply-btn"
                [disabled]="selectedDays.size === 0"
                (click)="onApply()">
          <mat-icon>check</mat-icon>
          Apply to {{ selectedDays.size }} Day(s)
        </button>
      </mat-dialog-actions>

    </div>
  `,
  styles: [`
    .week-schedule-dialog { font-family: inherit; }

    [mat-dialog-title].dialog-title {
      display: flex; align-items: flex-start; gap: 10px;
      padding: 18px 20px 14px; border-bottom: 1px solid #e2e8f0;
      background: #f8fafc; margin: 0;
    }
    .dialog-title mat-icon:first-child { color: var(--theme-primary); margin-top: 2px; flex-shrink: 0; }
    .dialog-title h3 { font-size: 1rem; font-weight: 700; margin: 0; }
    .dialog-title p { font-size: 0.75rem; color: #64748b; margin: 2px 0 0; }
    .close-btn { margin-left: auto; flex-shrink: 0; }

    mat-dialog-content { padding: 18px 20px; max-height: 65vh; }

    .info-banner {
      display: flex; align-items: flex-start; gap: 8px;
      background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px;
      padding: 10px 14px; font-size: 0.77rem; color: #1e40af;
      margin-bottom: 16px; line-height: 1.5;
    }
    .info-banner mat-icon { font-size: 1rem; width: 1rem; height: 1rem; flex-shrink: 0; margin-top: 1px; }

    .section-label {
      font-size: 0.72rem; font-weight: 700; color: #64748b;
      text-transform: uppercase; letter-spacing: .05em; margin-bottom: 8px;
    }

    .day-selector { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 8px; }
    .day-chip {
      padding: 6px 12px; border-radius: 20px; cursor: pointer;
      border: 1.5px solid #e2e8f0; background: #fff;
      font-size: 0.8rem; font-weight: 600; color: #64748b;
      transition: all .15s; user-select: none;
    }
    .day-chip.selected { background: var(--theme-primary); color: #fff; border-color: var(--theme-primary); }
    .day-chip:hover:not(.selected) { border-color: var(--theme-primary); color: var(--theme-primary); }

    .day-quick-actions { display: flex; gap: 4px; margin-bottom: 4px; }
    .day-quick-actions button { font-size: 0.75rem; color: #64748b; }

    .my-divider { margin: 14px 0; }

    .full-width { width: 100%; }

    .time-pair-row { margin-bottom: 12px; }
    .time-pair-label { font-size: 0.7rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 4px; }
    .time-pair-fields { display: flex; align-items: center; gap: 8px; }
    .time-field { flex: 1; min-width: 0; }
    .pair-arrow { color: #64748b; flex-shrink: 0; }
    .time-icon { cursor: pointer; color: #64748b; font-size: 1rem; width: 1rem; height: 1rem; }

    .preview-section { margin-top: 8px; }
    .preview-chips { display: flex; flex-wrap: wrap; gap: 6px; }
    .preview-chip {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 4px 10px; border-radius: 20px;
      background: var(--theme-primary-a08); color: var(--theme-primary-hover);
      font-size: 0.75rem; font-weight: 600;
    }
    .preview-chip mat-icon { font-size: 0.85rem; width: 0.85rem; height: 0.85rem; }

    .no-days-warning {
      display: flex; align-items: center; gap: 6px;
      font-size: 0.78rem; color: #f59e0b; margin-top: 8px;
    }
    .no-days-warning mat-icon { font-size: 1rem; width: 1rem; height: 1rem; }

    mat-dialog-actions { padding: 14px 20px; border-top: 1px solid #e2e8f0; background: #f8fafc; }

    .apply-btn {
      background: var(--theme-primary); color: #fff;
      display: flex; align-items: center; gap: 4px;
    }
    .apply-btn:hover:not(:disabled) { background: var(--theme-primary-hover); }
    .apply-btn:disabled { opacity: .5; }
  `]
})
export class AddWeekScheduleDialogComponent implements OnInit {

  weekForm!: FormGroup;
  selectedDays: Set<number> = new Set([1, 2, 3, 4, 5]);

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<AddWeekScheduleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddWeekScheduleDialogData
  ) { }

  ngOnInit(): void {
    this.weekForm = this.fb.group({
      period: [''],
      isRestDay: [false],
      // Regular fields
      rampTime1: [''], rampTime2: [''],
      rampTime3: [''], rampTime4: [''],
      rampTime5: [''], rampTime6: [''],
      // Flexi fields
      startTime:             [''],
      endTime:               [''],
      allowedRegularMinutes: [null],
      allowedOTMinutes:      [null],
    });
  }

  toggleDay(value: number): void {
    if (this.selectedDays.has(value)) { this.selectedDays.delete(value); }
    else { this.selectedDays.add(value); }
  }

  selectWorkdays(): void { this.selectedDays = new Set([1, 2, 3, 4, 5]); }
  selectAll(): void { this.selectedDays = new Set(this.data.weekdays.map(d => d.value)); }
  clearAll(): void { this.selectedDays = new Set(); }

  get selectedDaysArray(): number[] {
    return Array.from(this.selectedDays).sort((a, b) => a - b);
  }

  getDayName(value: number): string {
    return this.data.weekdays.find(d => d.value === value)?.name ?? '';
  }

  onCancel(): void { this.dialogRef.close(null); }

  onApply(): void {
    if (this.selectedDays.size === 0) return;
    const v = this.weekForm.value;

    const payloads = Array.from(this.selectedDays).map(weekDay => {
      if (this.data.isFlexi) {
        return {
          cbpartnerID:            this.data.employeeID,
          scheduleID:             0,
          period:                 v.period || '',
          weekDay,
          StartTime:              v.startTime,
          EndTime:                v.endTime,
          RAMP_AllowedRegMinutes: v.allowedRegularMinutes,
          RAMP_Allowed_OT:        v.allowedOTMinutes,
        };
      }
      return {
        cbpartnerID:     this.data.employeeID,
        scheduleID:      0,
        period:          v.period || '',
        weekDay,
        isRestDay:       v.isRestDay,
        isCustomWorkday: false,
        validFrom:       '',
        validTo:         '',
        customWorkdayDate: '',
        rampTime1: v.rampTime1, rampTime2: v.rampTime2,
        rampTime3: v.rampTime3, rampTime4: v.rampTime4,
        rampTime5: v.rampTime5, rampTime6: v.rampTime6,
      };
    });

    console.log('Week schedule payloads:', JSON.stringify(payloads, null, 2));
    this.dialogRef.close(payloads);
  }
}
